import type { Message } from "@nexus/types"
import { createAsyncThunk } from "@reduxjs/toolkit"

import { loadChatMessages } from "@/common/services/chat"
import type { ReduxMessage } from "@/common/types/chat"
import { handleError } from "@/common/utils/error-handler"

interface LoadMessagesParams {
  chatId: string
  context?: { chatRoomId?: string }
}

interface LoadMessagesResult {
  chatId: string
  messages: ReduxMessage[]
  chatRoomId: string | undefined
  hasMore: boolean
}

/**
 * Load chat messages thunk with pagination support
 * Fetches the most recent messages (newest first, then reversed for display)
 */
export const loadMessages = createAsyncThunk(
  "playgroundChat/loadMessages",
  async ({ chatId, context }: LoadMessagesParams): Promise<LoadMessagesResult> => {
    try {
      // Need chatRoomId to fetch messages from backend
      if (context?.chatRoomId) {
        const response = await loadChatMessages({
          chatRoomId: context.chatRoomId,
          limit: 50,
          order: "desc" // Get newest messages first
        })

        // Response is now { messages, hasMore, count }
        const messages = (response.messages || []) as Message[]
        const hasMore = response.hasMore ?? false

        // Reverse to display in chronological order (oldest to newest)
        // ReduxMessage = Omit<Message, "user" | "agent"> - messages from API match this
        const reversedMessages = messages.reverse() as ReduxMessage[]
        return { chatId, messages: reversedMessages, chatRoomId: context.chatRoomId, hasMore }
      }

      return { chatId, messages: [], chatRoomId: undefined, hasMore: false }
    } catch (error) {
      handleError(error, {
        component: "loadMessagesThunk",
        operation: "loadMessages",
        silent: true
      })
      return { chatId, messages: [], chatRoomId: undefined, hasMore: false }
    }
  }
)
